import {Injectable, ViewContainerRef} from '@angular/core';
import {CapacityConstraintAlertComponent} from '../components/capacity-constraint-alert/capacity-constraint-alert.component';
import {ConflictDialogComponent} from '../components/conflict-dialog/conflict-dialog.component';
import {DialogAction, DialogCloseResult, DialogService} from '@progress/kendo-angular-dialog';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

/** What the user chose when saving an override. null means they backed out entirely. */
export type OverrideSendChoice = 'send' | 'saveOnly' | null;

const SAVE_ONLY = 'Save Only';
const SAVE_AND_SEND = 'Save & Send Now';

@Injectable({
    providedIn: 'root'
})
export class AlertDialogService {

    constructor(private dialogService: DialogService) {
    }

    /**
     * Ask whether an override should reach the reservation system now (RLP-4189).
     *
     * Three-way rather than yes/no, because "No" still saves — only the X/Esc and Cancel
     * paths abandon the change, which is how every other dialog in this app behaves.
     *
     * @param parentOd when set, this O&D is a child in an OD group. Only the parent
     *  carries limits, so sending is not offered at all rather than offered and refused.
     */
    public openSendToRarsDialog(container: ViewContainerRef,
                                parentOd?: string): Observable<OverrideSendChoice> {
        const actions: DialogAction[] = parentOd
            ? [{text: 'Cancel'}, {text: SAVE_ONLY, primary: true}]
            : [{text: 'Cancel'}, {text: SAVE_ONLY}, {text: SAVE_AND_SEND, primary: true}];

        const dialogRef = this.dialogService.open({
            title: 'Send to Reservation System',
            width: 520,
            appendTo: container,
            content: parentOd
                ? `This O&D is part of OD group ${parentOd}. Limits are sent to the `
                  + `reservation system under ${parentOd}, so this override cannot be sent `
                  + 'on its own — it will go with the next scheduled export.'
                : 'Send this override to the reservation system (RARS) now? '
                  + 'Choosing Save Only keeps the current behaviour: the override is saved '
                  + 'and sent with the next scheduled export.',
            actions
        });

        return dialogRef.result.pipe(
            map(result => {
                if (result instanceof DialogCloseResult) {
                    return null;
                }
                switch ((result as DialogAction).text) {
                    case SAVE_AND_SEND:
                        return 'send';
                    case SAVE_ONLY:
                        return 'saveOnly';
                    default:
                        return null;
                }
            })
        );
    }


    public openConstraintAlertDialog(request: any, container: ViewContainerRef) {
        const dialogRef = this.dialogService.open({
            width: 320,
            appendTo: container,
            content: CapacityConstraintAlertComponent,
            actions: [
                { text: 'Cancel' },
                { text: 'Submit' }
            ]
        });
        const alertDialog = dialogRef.content.instance as CapacityConstraintAlertComponent;
        alertDialog.constraintAlert = request;
        return dialogRef;
    }

    public openReminderDeleteAlertDialog(container: ViewContainerRef) {
        return this.dialogService.open({
            appendTo: container,
            content: 'Are you sure you want to delete this reminder?',
            actions: [
                {text: 'No'},
                {text: 'Yes', primary: true}
            ]
        });
    }

    public openConflictDialog(message: string, container: ViewContainerRef) {
        const dialogRef = this.dialogService.open({
            appendTo: container,
            content: ConflictDialogComponent,
            actions: [
                {text: 'Reload', primary: true}
            ]
        });
        const conflictDialog = dialogRef.content.instance as ConflictDialogComponent;
        conflictDialog.message = message;
        return dialogRef;
    }
}
